import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useActivityStore } from '../../stores/activityStore';
import { useAuthStore } from '../../stores/authStore';
import { ChevronLeft, ChevronRight, Users, Loader2, Info } from 'lucide-react';
import clsx from 'clsx';
import ActivityModal, { ImageWithFallback } from '../common/ActivityModal';
import { isFull, selectionAccueil } from '../../lib/places';
import { BadgePlaces } from '../common/DisponibilitePlaces';
import { diversifierAccueil, libelleAge, trancheAge } from '../../lib/accueil';

interface PopularStagesProps {
  limit?: number;
}

const PopularStages = ({ limit = 12 }: PopularStagesProps) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    activities, 
    centers, 
    fetchActivities, 
    fetchCenters,
    isLoading,
    error
  } = useActivityStore();
  
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [selectedAge, setSelectedAge] = useState<string>('');
  const [selectedSemaine, setSelectedSemaine] = useState<string>('');
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [filteredActivities, setFilteredActivities] = useState<any[]>([]);
  const [toutComplet, setToutComplet] = useState(false);
  const [voirTout, setVoirTout] = useState(false);
  const carrouselRef = useRef<HTMLDivElement>(null);
  const [peutReculer, setPeutReculer] = useState(false);
  const [peutAvancer, setPeutAvancer] = useState(false);
  const [carteActive, setCarteActive] = useState(0);
  const [semaineOptions, setSemaineOptions] = useState<{label: string, value: string}[]>([
    { label: 'Toutes les semaines', value: '' }
  ]);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Age ranges for the filter
  const ageRanges = [
    { label: 'Tous les âges', value: '' },
    { label: '2,5 - 4 ans', value: '2.5-4' },
    { label: '4 - 6 ans', value: '4-6' },
    { label: '6 - 8 ans', value: '6-8' },
    { label: '8 - 12 ans', value: '8-12' },
    { label: '12 - 13 ans', value: '12-13' }
  ];

  useEffect(() => {
    // Fetch activities and centers on component mount
    fetchActivities();
    fetchCenters();
  }, [fetchActivities, fetchCenters]);

  useEffect(() => {
    // Extract unique semaines from activities and sort them
    if (activities.length > 0) {
      const uniqueSemaines = [...new Set(activities.map(a => a.semaine).filter(Boolean))];
      const sortedSemaines = uniqueSemaines.sort();
      
      const options = [
        { label: 'Toutes les semaines', value: '' },
        ...sortedSemaines.map(semaine => ({ 
          label: semaine, 
          value: semaine 
        }))
      ];
      
      setSemaineOptions(options);
    }
  }, [activities]);

  useEffect(() => {
    // Filter activities based on selected filters
    let filtered = [...activities];
    
    // Filter by center
    if (selectedCenter) {
      filtered = filtered.filter(activity => activity.center_id === selectedCenter);
    }
    
    // Filter by age range
    if (selectedAge) {
      const [minAge, maxAge] = selectedAge.split('-').map(Number);
      filtered = filtered.filter(activity => {
        const tranche = trancheAge(activity.stage);
        return tranche.min <= maxAge && tranche.max >= minAge;
      });
    }
    
    // Filter by semaine
    if (selectedSemaine) {
      filtered = filtered.filter(activity => activity.semaine === selectedSemaine);
    }
    
    // Filter out past activities
    const today = new Date();
    filtered = filtered.filter(activity => {
      const endDate = new Date(activity.end_date);
      return endDate >= today;
    });
    
    // Disponibles d'abord, puis complets ; date de début, puis places restantes
    const selection = selectionAccueil(filtered, limit);
    // Sans filtre : une carte par tranche d'âge, en variant les centres
    const sansFiltre = !selectedCenter && !selectedAge && !selectedSemaine;
    setFilteredActivities(
      sansFiltre ? diversifierAccueil(filtered).slice(0, limit) : selection.cartes
    );
    setToutComplet(selection.toutComplet);
    setVoirTout(selection.voirTout);
  }, [activities, selectedCenter, selectedAge, selectedSemaine, limit]);

  // Les flèches ne s'affichent que s'il reste quelque chose à faire défiler
  const majFleches = useCallback(() => {
    const el = carrouselRef.current;
    if (!el) return;
    setPeutReculer(el.scrollLeft > 4);
    setPeutAvancer(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    const carte = el.querySelector<HTMLElement>('[data-carte]');
    if (carte) setCarteActive(Math.round(el.scrollLeft / (carte.offsetWidth + 24)));
  }, []);

  useEffect(() => {
    const el = carrouselRef.current;
    if (el) el.scrollLeft = 0;
    majFleches();
    window.addEventListener('resize', majFleches);
    return () => window.removeEventListener('resize', majFleches);
  }, [filteredActivities, voirTout, majFleches]);

  const defiler = (sens: 1 | -1) => {
    const el = carrouselRef.current;
    if (!el) return;
    const carte = el.querySelector<HTMLElement>('[data-carte]');
    const pas = carte ? carte.offsetWidth + 24 : el.clientWidth;
    el.scrollBy({ left: sens * pas, behavior: 'smooth' });
  };

  const allerA = (index: number) => {
    const el = carrouselRef.current;
    const carte = el?.querySelector<HTMLElement>('[data-carte]');
    if (!el || !carte) return;
    el.scrollTo({ left: index * (carte.offsetWidth + 24), behavior: 'smooth' });
  };

  const resetFilters = () => {
    setSelectedCenter('');
    setSelectedAge('');
    setSelectedSemaine('');
  };

  const handleOpenModal = (activity: any) => {
    setSelectedActivity(activity);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center md:flex-row md:items-center md:justify-between md:text-left mb-12">
          <div>
            <h2 className="text-4xl text-gray-900 mb-4">Nos prochains stages</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto md:mx-0">
              Les prochains stages et plaines, pour tous les âges et dans tous nos centres
            </p>
          </div>
          <Link
            to="/activities"
            className="mt-4 md:mt-0 text-primary-600 hover:text-primary-700 font-semibold flex items-center"
          >
            Voir tous les stages
            <ChevronRight className="ml-1 w-5 h-5" />
          </Link>
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Centre
              </label>
              <select
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
                className="form-input w-full"
              >
                <option value="">Tous les centres</option>
                {centers.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Âge de l'enfant
              </label>
              <select
                value={selectedAge}
                onChange={(e) => setSelectedAge(e.target.value)}
                className="form-input w-full"
              >
                {ageRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Semaine
              </label>
              <select
                value={selectedSemaine}
                onChange={(e) => setSelectedSemaine(e.target.value)}
                className="form-input w-full"
              >
                {semaineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {(selectedCenter || selectedAge || selectedSemaine) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <p className="text-red-700">Une erreur est survenue lors du chargement des stages.</p>
            <button 
              onClick={() => fetchActivities()}
              className="mt-4 btn-primary"
            >
              Réessayer
            </button>
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun stage disponible</h3>
            <p className="text-gray-600 mb-4">
              Aucun stage n'est disponible pour le moment. Revenez bientôt pour découvrir notre programmation.
            </p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun stage ne correspond à vos critères</h3>
            <p className="text-gray-600 mb-4">
              Veuillez modifier vos filtres pour trouver des stages disponibles.
            </p>
            <button
              onClick={resetFilters}
              className="btn-primary"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <>
            {toutComplet && (
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 mt-0.5 text-amber-600 flex-shrink-0" />
                  <p className="text-amber-800 font-medium">Tous les stages sont actuellement complets.</p>
                </div>
                <Link
                  to="/activities"
                  className="text-primary-600 hover:text-primary-700 font-semibold flex items-center"
                >
                  Voir tous les stages
                  <ChevronRight className="ml-1 w-5 h-5" />
                </Link>
              </div>
            )}

            <div className="relative">
              {peutReculer && (
                <button
                  type="button"
                  onClick={() => defiler(-1)}
                  aria-label="Stages précédents"
                  className="flex absolute left-0 md:-left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg ring-4 ring-white hover:bg-primary-700 focus:outline-none focus-visible:ring-primary-300"
                >
                  <ChevronLeft className="h-7 w-7" />
                </button>
              )}
              {peutAvancer && (
                <button
                  type="button"
                  onClick={() => defiler(1)}
                  aria-label="Stages suivants"
                  className="flex absolute right-0 md:-right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg ring-4 ring-white hover:bg-primary-700 focus:outline-none focus-visible:ring-primary-300"
                >
                  <ChevronRight className="h-7 w-7" />
                </button>
              )}

              <div
                ref={carrouselRef}
                onScroll={majFleches}
                role="region"
                aria-roledescription="carrousel"
                aria-label="Stages à venir"
                tabIndex={0}
                className="sans-barre flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-xl"
              >
                {filteredActivities.map((activity) => {
                  const complet = isFull(activity);

                  return (
                    <div
                      key={activity.id}
                      data-carte
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenModal(activity)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenModal(activity);
                        }
                      }}
                      aria-label={`${activity.stage.title}${complet ? ' — complet' : ''}`}
                      className={clsx(
                        'snap-start flex-shrink-0 w-[80%] sm:w-[calc((100%-1.5rem)/2.2)] lg:w-[calc((100%-4.5rem)/4.2)] bg-white rounded-xl shadow-md overflow-hidden cursor-pointer transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                        !complet && 'hover:shadow-lg'
                      )}
                    >
                      <div className="relative">
                        {/* Le gris porte sur l'image et le texte, pas sur le badge « Complet » */}
                        <ImageWithFallback
                          src={activity.stage.image_url}
                          alt={activity.stage.title}
                          className={clsx('w-full h-48 object-cover', complet && 'opacity-60 grayscale')}
                        />
                        <BadgePlaces session={activity} />
                      </div>

                      <div className={clsx('p-4', complet && 'opacity-60 grayscale')}>
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">{activity.stage.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          {new Date(activity.start_date).toLocaleDateString('fr-FR')} - {new Date(activity.end_date).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          {activity.center.name}
                        </p>
                        <div className="flex justify-between items-center mt-3">
                          <span className="text-primary-600 font-bold">
                            {activity.calculated_main_price || activity.prix_normal} €
                          </span>
                          <span className="text-xs text-gray-500 flex items-center">
                            <Users className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                            {libelleAge(trancheAge(activity.stage))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {voirTout && (
                  <Link
                    to="/activities"
                    data-carte
                    className="snap-start flex-shrink-0 w-[80%] sm:w-[calc((100%-1.5rem)/2.2)] lg:w-[calc((100%-4.5rem)/4.2)] flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-primary-200 bg-white p-6 text-primary-600 hover:border-primary-400 hover:text-primary-700 font-semibold"
                  >
                    <ChevronRight className="h-10 w-10" />
                    Voir tous les stages
                  </Link>
                )}
              </div>

              {/* Les points disent qu'il y a plus à voir, et où l'on en est */}
              {(peutReculer || peutAvancer) && (
                <div className="mt-4 flex justify-center gap-2" aria-hidden="true">
                  {filteredActivities.map((activity, i) => (
                    <button
                      key={activity.id}
                      type="button"
                      tabIndex={-1}
                      onClick={() => allerA(i)}
                      className={clsx(
                        'h-2.5 rounded-full transition-all',
                        i === carteActive ? 'w-6 bg-primary-600' : 'w-2.5 bg-primary-200 hover:bg-primary-300'
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Activity Details Modal */}
      <ActivityModal 
        activity={selectedActivity}
        isOpen={isModalOpen && selectedActivity !== null}
        onClose={handleCloseModal}
      />
    </section>
  );
};

export default PopularStages;